import type { PaymentKind, PaymentMethod, PaymentStatus } from '@salonomy/types';
import type { PillTone } from '@salonomy/ui';

/**
 * Presentation-only labels + Pill tones for the takings ledger (WS6). NONE of this is money logic — it
 * maps the server's enum values to human text and a colour. The actual amounts/refundable figures are
 * always read straight off the Payment DTO; this file never touches money.
 */

export const PAYMENT_KIND_LABELS: Record<PaymentKind, string> = {
  deposit: 'Deposit',
  balance: 'Balance',
  no_show_fee: 'No-show fee',
  retail: 'Retail',
  full: 'Full payment',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  online: 'Online',
  terminal: 'Terminal',
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  requires_payment_method: 'Awaiting payment',
  requires_action: 'Action required',
  processing: 'Processing',
  succeeded: 'Succeeded',
  failed: 'Failed',
  canceled: 'Canceled',
  refunded: 'Refunded',
  partially_refunded: 'Partly refunded',
};

export function paymentStatusTone(status: PaymentStatus): PillTone {
  switch (status) {
    case 'succeeded':
      return 'success';
    case 'failed':
    case 'canceled':
      return 'danger';
    case 'refunded':
    case 'partially_refunded':
      return 'soft';
    default:
      return 'neutral';
  }
}

/**
 * A refund is only allowed by the API when the payment is `succeeded` or `partially_refunded` (else the
 * server 409s). The UI reads this to GATE the Refund action off rather than firing a guaranteed-409.
 */
export function isRefundable(status: PaymentStatus): boolean {
  return status === 'succeeded' || status === 'partially_refunded';
}
